import React from 'react';

import { ButtonBase, Typography } from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';

import { Link } from 'react-router-dom';
import { useUserLogContext } from 'context/UserLogContext/UserLogContext';

const useStyles = makeStyles(theme => ({
  button: {
    display: 'block',
  },
  card: {
    position: 'relative',
    padding: theme.spacing(8),
    height: '100%',
    width: '100%',
    boxShadow: theme.shadows[3],
    cursor: ({ clickable }) => (clickable ? 'pointer' : 'auto'),

    transition: theme.transitions.create(['box-shadow'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.standard,
    }),

    '&:hover': {
      boxShadow: theme.shadows[5],
    },

    '@media(max-width: 740px)': {
      padding: ({ denseMobile }) => (denseMobile ? theme.spacing(4.5, 3.5) : theme.spacing(7)),
    },
    [theme.breakpoints.down('xs')]: {
      padding: ({ denseMobile }) => (denseMobile ? theme.spacing(4, 3) : theme.spacing(7, 5)),
    },
    '& h2': {
      padding: theme.spacing(6, 0),
      color: ({ disabled }) => (disabled ? theme.palette.text.disabled : theme.palette.primary.main),
      fontSize: '1.75rem',
      fontWeight: 700,
      textAlign: 'center',
      '@media(max-width: 740px)': {
        fontSize: ({ denseMobile }) => (denseMobile ? '1.15rem' : '1.35rem'),
        padding: ({ denseMobile }) => (denseMobile ? theme.spacing(2.5, 0) : theme.spacing(5, 0)),
      },
    },
  },
  additionalText: {
    position: 'absolute',
    width: '100%',
    bottom: theme.spacing(4),
    left: 0,
    textAlign: 'center',
    fontSize: '.8rem',
    color: theme.palette.text.disabled,
    '@media(max-width: 740px)': {
      bottom: ({ denseMobile }) => (denseMobile ? theme.spacing(2.5) : theme.spacing(4)),
      fontSize: ({ denseMobile }) => (denseMobile ? '.72rem' : '.8rem'),
    },
  },
  title: {
    position: 'absolute',
    top: theme.spacing(6),
    left: theme.spacing(6),
    textAlign: 'center',
    fontSize: '.875rem',
    fontWeight: 700,
    color: theme.palette.text.disabled,
    '@media(max-width: 740px)': {
      top: ({ denseMobile }) => (denseMobile ? theme.spacing(3) : theme.spacing(4)),
      left: ({ denseMobile }) => (denseMobile ? theme.spacing(3) : theme.spacing(4)),
    },
  },
  link: {
    height: '100%',
    width: '100%',
  },
}));

const DashboardCard = ({ children, additionalText, title, to, className, disabled, ariaLabel, clickable, denseMobile }) => {
  const { logEvent } = useUserLogContext();
  const onClick = () => {
    logEvent('dashboard-click', { button: ariaLabel });
  };
  const classes = useStyles({ disabled, clickable, denseMobile });
  const cardContents = (
    <Paper className={classes.card} square>
      <Typography className={classes.title}>{title}</Typography>
      {children}
      <Typography className={classes.additionalText}>{additionalText}</Typography>
    </Paper>
  );
  const rootClassName = clsx(classes.button, classes.link, className);
  const hasDestination = Boolean(to);

  if (disabled || !hasDestination) {
    return (
      <ButtonBase aria-label={ariaLabel} className={rootClassName} component='div' disabled={disabled} disableRipple tabIndex={-1}>
        {cardContents}
      </ButtonBase>
    );
  }

  return (
    <ButtonBase
      aria-label={ariaLabel}
      className={rootClassName}
      component={Link}
      disableRipple={!clickable}
      onClick={onClick}
      to={to}
    >
      {cardContents}
    </ButtonBase>
  );
};

DashboardCard.defaultProps = {
  to: null,
  className: '',
  disabled: false,
  additionalText: '',
  title: '',
  clickable: true,
  denseMobile: false,
};

export default DashboardCard;
